import { useEffect } from "react";
import { BlockDefinition, ContainerBlockDefinition, isInStudio } from "@softr-io/blocks-sdk";
import { isBlockReady } from "@/useBlockReadiness";
import { isInPreviewIframe, timeout } from "@softr/utils";

interface useScrollToBlockProps {
    definitions: readonly BlockDefinition[];
}

/**
 * This hook is used to scroll to the block that is specified in the URL hash.
 * If the target block is a tab, it will scroll to the parent tab container.
 * */
export function useScrollToBlock({ definitions }: useScrollToBlockProps) {
    useEffect(() => {
        const abortController = new AbortController();
        waitForAndScrollTo(definitions, abortController);
        return () => abortController.abort();
    }, [definitions]);
}

function isBlockLoaded(blockDefinition: BlockDefinition): boolean {
    return (
        isBlockReady(blockDefinition) &&
        // Note that the block root, i.e. the element with id=hrid, is rendered pretty early
        // even when the block's manifest or bundle haven't been loaded. It's basically an
        // empty shell. That's why we also need to wait for the element's ready events
        document.getElementById(blockDefinition.hrid) !== null
    );
}

async function waitForBlockLoaded(
    blockDefinition: BlockDefinition,
    { signal }: { signal: AbortSignal },
): Promise<void> {
    // isBlockLoaded only checks for the block's manifest and the bundle. It doesn't really know
    // anything about whether the block's body has been rendered in the dom
    // so we add a minimum grace period of 100ms for react to render the body.
    if (isBlockLoaded(blockDefinition)) {
        await timeout(100, { signal });
        return;
    }
    while (!isBlockLoaded(blockDefinition)) {
        if (signal.aborted) {
            throw signal.reason;
        }
        await new Promise((resolve) => setTimeout(resolve, 100));
    }
    await timeout(100, { signal });
}

async function waitForAndScrollTo(definitions: readonly BlockDefinition[], controller: AbortController) {
    if (isInStudio()) return;
    // 1. Find the root level block under whom the target block is (in case it's a nested block, that's the tab
    // container, otherwise, it's the block itself)
    // 2. Wait for all the blocks above it to be loaded so that the page layout is semi stable (semi because
    // we're not waiting for the loading skeletons to resolve)
    // 3. Scroll to the block
    const urlHash = isInPreviewIframe() ? parent.location.hash : window.location.hash;
    const blockHrid = urlHash?.substring(1);
    if (!blockHrid) return;
    const blockDef = definitions.find((def) => def.hrid === blockHrid);
    if (!blockDef) return;
    let done = false;
    const signal = controller.signal;
    // Abort the whole thing after 2 seconds because if it takes too long, we really don't want to
    // move the scroll position too late after the user has landed on the page
    setTimeout(() => {
        console.warn(`Timed out waiting for block with hrid ${blockHrid} to load`);
        controller.abort(new Error(`Timed out waiting for block with hrid ${blockHrid} to load`));
        if (!done) {
            // Even though we've timed out waiting for the blocks above, we can still make a best effort
            // attempt to scroll to the block itself if it exists in the dom.
            document.getElementById(blockHrid)?.scrollIntoView({
                behavior: "instant",
            });
        }
    }, 3000);

    const rootBlockDef = getParentContainer(blockDef.id, definitions) ?? blockDef;

    const rootBlockIndex = definitions.indexOf(rootBlockDef);
    if (rootBlockIndex === -1) return;

    const blocksBefore = definitions.slice(0, rootBlockIndex);

    await Promise.all(blocksBefore.map((def) => waitForBlockLoaded(def, { signal })));

    const elem = document.getElementById(rootBlockDef.hrid);
    if (!elem) return;

    // The above waiting logic still isn't perfect because it only waits for the block's manifest and bundle to load
    // + a grace period, which is enough for the skeletons to show up, but doesn't guarantee that the actual data
    // is loaded. So, the scrolled to position will shift as the blocks above replace their skeletons with actual data.
    // Not much we can do about that at the moment.
    elem.scrollIntoView({
        behavior: "instant",
    });
    done = true;
}

/**
 * Get the parent container of the block with the given id.
 * */
function getParentContainer(
    blockId: string | undefined,
    definitions: readonly BlockDefinition[],
): ContainerBlockDefinition | undefined {
    return definitions.find(
        (definition): definition is ContainerBlockDefinition =>
            definition.type === "container" && !!definition.container.slots.find((slot) => slot.blockId === blockId),
    );
}
